import { useCallback, useMemo, useState } from 'react'
import { Alert, Button, Card, Empty, Form, Input, Modal, Select, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useNotificationList,
  useNotificationOptionOrganisations,
  useNotificationOptionsList,
  useSaveNotificationListMutation,
  useUpdateNotificationSampleMutation,
} from '../../hooks/useReports'
import { useAuthSession } from '../../lib/session'
import type { NotificationListItem } from '../../types/reports'
import { useToast } from '../../contexts/ToastContext'
import { getSelectedUtcTime } from '../../features/reports/schedule/time'
import { getPlatformTypeLabel } from './reportOptionConstants'

const zeroGuid = '00000000-0000-0000-0000-000000000000'

const SCHEDULE_OPTIONS = [
  { value: 'D', label: 'Daily' },
  { value: 'W', label: 'Weekly' },
  { value: 'M', label: 'Monthly' },
  { value: 'NOW', label: 'Now' },
]

const WEEKLY_OPTIONS = [
  { value: 'Mon', label: 'Monday' },
  { value: 'Tue', label: 'Tuesday' },
  { value: 'Wed', label: 'Wednesday' },
  { value: 'Thu', label: 'Thursday' },
  { value: 'Fri', label: 'Friday' },
  { value: 'Sat', label: 'Saturday' },
  { value: 'Sun', label: 'Sunday' },
]

const MONTHLY_OPTIONS = Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))
const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const value = String(i + 1).padStart(2, '0')
  return { value, label: value }
})
const MINUTE_OPTIONS = ['00', '15', '30', '45'].map((value) => ({ value, label: value }))
const PERIOD_OPTIONS = [
  { value: 'AM', label: 'AM' },
  { value: 'PM', label: 'PM' },
]
const FILE_TYPE_OPTIONS = [
  { value: 'csv', label: 'CSV' },
  { value: 'pdf', label: 'PDF' },
  { value: 'xlsx', label: 'Excel' },
]

type AddScheduleFormValues = {
  schedule: string
  frequency?: string
  hour: string
  minute: string
  period: 'AM' | 'PM'
  fileType: string
  companyId?: string
}

export default function NotificationOptionSchedulesPage() {
  const { notificationOptionID = '' } = useParams<{ notificationOptionID: string }>()
  const session = useAuthSession()
  const navigate = useNavigate()
  const { addToast } = useToast()

  const [createOpen, setCreateOpen] = useState(false)
  const [form] = Form.useForm<AddScheduleFormValues>()

  const clientId = session?.currentClientId || ''

  const optionsQuery = useNotificationOptionsList(clientId)
  const selectedOption = useMemo(
    () => (optionsQuery.data || []).find((option) => option.notificationOptionID === notificationOptionID) || null,
    [optionsQuery.data, notificationOptionID]
  )

  const listQuery = useNotificationList(clientId, notificationOptionID)
  const organisationsQuery = useNotificationOptionOrganisations(clientId, selectedOption?.platformType || 0)
  const sampleMutation = useUpdateNotificationSampleMutation()
  const saveScheduleMutation = useSaveNotificationListMutation()

  const scheduleType = Form.useWatch('schedule', form)

  const confirmGenerateSample = useCallback((notificationListID: string) => {
    Modal.confirm({
      title: 'Generate Sample Report',
      content: "This will start a sample report generation job. You'll receive it by email once it completes.",
      okText: 'Generate',
      onOk: async () => {
        const ok = await sampleMutation.mutateAsync({ notificationListID, clientId })
        if (ok) {
          addToast("Sample report generation started. You'll receive it by email shortly.", 'success')
          return
        }
        addToast('Failed to generate sample report.', 'error')
      },
    })
  }, [addToast, clientId, sampleMutation])

  const columns = useMemo<ColumnsType<NotificationListItem>>(
    () => [
      {
        title: 'Title',
        dataIndex: 'title',
        key: 'title',
      },
      {
        title: 'Schedule',
        dataIndex: 'schedule',
        key: 'schedule',
      },
      {
        title: 'Recipients',
        dataIndex: 'recipients',
        key: 'recipients',
        render: (value: string[]) => (value?.length ? value.join(', ') : '-'),
      },
      {
        title: 'Created By',
        dataIndex: 'createdBy',
        key: 'createdBy',
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 180,
        render: (_, row) => (
          <Button onClick={() => confirmGenerateSample(row.notificationListID)} loading={sampleMutation.isPending}>
            Generate Sample
          </Button>
        ),
      },
    ],
    [confirmGenerateSample, sampleMutation]
  )

  const handleOpenCreate = () => {
    form.resetFields()
    form.setFieldsValue({
      schedule: 'D',
      hour: '08',
      minute: '00',
      period: 'AM',
      fileType: 'csv',
      companyId: organisationsQuery.data?.[0]?.companyId,
    })
    setCreateOpen(true)
  }

  const handleCreateSchedule = async () => {
    if (!selectedOption) {
      addToast('Notification option is not available.', 'error')
      return
    }

    try {
      const values = await form.validateFields()
      const scheduleCode =
        values.schedule === 'W' || values.schedule === 'M' ? `${values.schedule}-${values.frequency || ''}` : values.schedule

      const ok = await saveScheduleMutation.mutateAsync({
        notificationList: {
          notificationListID: zeroGuid,
          notificationOptionID,
          clientId,
          schedule: scheduleCode,
          scheduleTime: getSelectedUtcTime(values.hour, values.minute, values.period),
          fileType: values.fileType,
          companyId: String(values.companyId || ''),
          cc: [],
          subject: '',
          body: '',
          recipients: [],
        },
      })

      if (!ok) {
        addToast('Failed to save schedule.', 'error')
        return
      }

      addToast('Schedule created successfully.', 'success')
      setCreateOpen(false)
      await listQuery.refetch()
    } catch (error) {
      if (error instanceof Error) {
        addToast(error.message, 'error')
      }
    }
  }

  return (
    <Space direction="vertical" size={16} className="w-full">
      <Card>
        <Space className="w-full justify-between" align="start">
          <div>
            <Typography.Title level={3} className="!mb-1">
              Scheduled Reports {selectedOption ? `- ${selectedOption.name}` : ''}
            </Typography.Title>
            <Typography.Text type="secondary">
              Notification option scope for current client.
              {selectedOption?.platformType ? ` Platform: ${getPlatformTypeLabel(selectedOption.platformType)}.` : ''}
            </Typography.Text>
          </div>
          <Space>
            <Button type="primary" onClick={handleOpenCreate} disabled={!selectedOption}>
              Add Schedule
            </Button>
            <Button onClick={() => navigate('/reports/manage/options')}>Back to Manage Options</Button>
          </Space>
        </Space>
      </Card>

      {selectedOption?.platformType === 1 ? (
        <Card>
          <Typography.Text strong>Xero Organisations</Typography.Text>
          {organisationsQuery.isError ? (
            <Alert
              type="warning"
              showIcon
              message="Unable to load organisations for this option"
              description={organisationsQuery.error instanceof Error ? organisationsQuery.error.message : 'Unknown error'}
            />
          ) : null}
          {!organisationsQuery.isError && (organisationsQuery.data || []).length === 0 ? (
            <Empty description="No organisations available" />
          ) : null}
          <Space wrap className="mt-3">
            {(organisationsQuery.data || []).map((item) => (
              <Tag key={item.companyId}>{item.companyName}</Tag>
            ))}
          </Space>
        </Card>
      ) : null}

      {listQuery.isError ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load notification schedule list"
          description={listQuery.error instanceof Error ? listQuery.error.message : 'Unknown error'}
          action={<Button onClick={() => listQuery.refetch()}>Retry</Button>}
        />
      ) : null}

      {!listQuery.isError && !listQuery.isLoading && (listQuery.data || []).length === 0 ? (
        <Card>
          <Empty description="No schedules found for this option" />
        </Card>
      ) : null}

      <Card>
        <Table<NotificationListItem>
          rowKey="notificationListID"
          loading={listQuery.isLoading}
          columns={columns}
          dataSource={listQuery.data || []}
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
        />
      </Card>

      <Modal
        title="Add Schedule"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => void handleCreateSchedule()}
        okText="Save"
        okButtonProps={{ loading: saveScheduleMutation.isPending }}
      >
        <Form<AddScheduleFormValues>
          form={form}
          layout="vertical"
          initialValues={{ schedule: 'D', hour: '08', minute: '00', period: 'AM', fileType: 'csv' }}
        >
          <Space className="w-full" wrap>
            <Form.Item name="schedule" label="Schedule" rules={[{ required: true, message: 'Schedule is required' }]}>
              <Select style={{ width: 170 }} options={SCHEDULE_OPTIONS} />
            </Form.Item>
            {scheduleType === 'W' ? (
              <Form.Item name="frequency" label="Day of Week" rules={[{ required: true, message: 'Frequency is required' }]}>
                <Select style={{ width: 170 }} options={WEEKLY_OPTIONS} />
              </Form.Item>
            ) : null}
            {scheduleType === 'M' ? (
              <Form.Item name="frequency" label="Day of Month" rules={[{ required: true, message: 'Frequency is required' }]}>
                <Select style={{ width: 170 }} options={MONTHLY_OPTIONS} />
              </Form.Item>
            ) : null}
            {scheduleType !== 'NOW' ? (
              <>
                <Form.Item name="hour" label="Hour" rules={[{ required: true, message: 'Hour is required' }]}>
                  <Select style={{ width: 110 }} options={HOUR_OPTIONS} />
                </Form.Item>
                <Form.Item name="minute" label="Minute" rules={[{ required: true, message: 'Minute is required' }]}>
                  <Select style={{ width: 110 }} options={MINUTE_OPTIONS} />
                </Form.Item>
                <Form.Item name="period" label="Period" rules={[{ required: true, message: 'Period is required' }]}>
                  <Select style={{ width: 110 }} options={PERIOD_OPTIONS} />
                </Form.Item>
              </>
            ) : null}
          </Space>

          {selectedOption?.platformType === 1 ? (
            <Form.Item
              name="companyId"
              label="Organisation"
              rules={[{ required: (organisationsQuery.data || []).length > 0, message: 'Organisation is required' }]}
            >
              <Select
                options={(organisationsQuery.data || []).map((item) => ({ value: item.companyId, label: item.companyName }))}
                notFoundContent="No organisations"
              />
            </Form.Item>
          ) : null}

          <Form.Item name="fileType" label="File Type" rules={[{ required: true, message: 'File type is required' }]}>
            <Select options={FILE_TYPE_OPTIONS} />
          </Form.Item>

          <Form.Item label="Title">
            <Input value={selectedOption?.title || selectedOption?.name || ''} readOnly />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  )
}
